import {
    browserLocalPersistence,
    createUserWithEmailAndPassword,
    onAuthStateChanged,
    setPersistence,
    signInWithEmailAndPassword,
    signOut
} from 'firebase/auth';
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { auth } from '../firebase';
import { getUserByUid } from '../services/firestoreService';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// Configuration constants
const INACTIVITY_TIMEOUT = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp'; // localStorage key for cross-session tracking

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ready, setReady] = useState(false);
    
    const inactivityTimerRef = useRef(null);

    const fetchUserData = async (user) => {
        try {
            const userDoc = await getUserByUid(user.uid);
            // Attach Firestore data to the original Firebase User object
            const enrichedUser = Object.assign(user, userDoc);
            setCurrentUser(enrichedUser);
        } catch (err) {
            console.error("❌ Failed to fetch user document:", err.message);
            setCurrentUser(user); // Preserve original user even if Firestore fails
        }
    };

    // Save last activity timestamp to localStorage
    const saveLastActivity = useCallback(() => {
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
        } catch (error) {
            console.error('Failed to save last activity timestamp:', error);
        }
    }, []);

    // Get last activity timestamp from localStorage
    const getLastActivity = useCallback(() => {
        try {
            const timestamp = localStorage.getItem(LAST_ACTIVITY_KEY);
            return timestamp ? parseInt(timestamp, 10) : null;
        } catch (error) {
            console.error('Failed to get last activity timestamp:', error);
            return null;
        }
    }, []);

    const logout = () => {
        // Clear last activity timestamp on logout
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        return signOut(auth);
    };

    // Clear all timers
    const clearInactivityTimers = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    }, []);

    // Handle automatic logout
    const handleAutoLogout = useCallback(async () => {
        clearInactivityTimers();
        // Clear last activity timestamp
        localStorage.removeItem(LAST_ACTIVITY_KEY);
        try {
            await logout();
            // Optionally show a toast notification
            console.log('You have been automatically logged out due to inactivity.');
        } catch (error) {
            console.error('Auto-logout failed:', error);
        }
    }, [clearInactivityTimers]);

    // Check if session has expired based on stored timestamp
    const checkSessionExpiry = useCallback(async () => {
        const lastActivity = getLastActivity();
        if (!lastActivity) {
            // No stored timestamp, treat as new session
            saveLastActivity();
            return false;
        }

        const timeSinceLastActivity = Date.now() - lastActivity;
        
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
            // Session expired, log out
            console.log('Session expired due to inactivity across browser sessions.');
            await handleAutoLogout();
            return true;
        }

        // Session still valid, update timestamp
        saveLastActivity();
        return false;
    }, [getLastActivity, saveLastActivity, handleAutoLogout]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Check if session expired while browser was closed
                const expired = await checkSessionExpiry();
                if (expired) {
                    // Session expired, user will be logged out
                    setCurrentUser(null);
                    setLoading(false);
                    setReady(true);
                    return;
                }
                
                await fetchUserData(user);
                // Save initial activity timestamp when user is detected
                saveLastActivity();
            } else {
                setCurrentUser(null);
                // Clear last activity timestamp when no user
                localStorage.removeItem(LAST_ACTIVITY_KEY);
            }
            setLoading(false);
            setReady(true);
        });

        return unsubscribe;
    }, [checkSessionExpiry, saveLastActivity]);

    const refreshUser = async () => {
        const user = auth.currentUser;
        if (user) {
            await fetchUserData(user);
        }
    };

    const ensurePersistence = async () => {
        await setPersistence(auth, browserLocalPersistence);
    };

    const signup = async (email, password) => {
        await ensurePersistence();
        const result = await createUserWithEmailAndPassword(auth, email, password);
        // Save activity timestamp on signup
        saveLastActivity();
        return result;
    };

    const login = async (email, password) => {
        await ensurePersistence();
        const result = await signInWithEmailAndPassword(auth, email, password);
        // Save activity timestamp on login
        saveLastActivity();
        return result;
    };

    // Reset inactivity timer
    const resetInactivityTimer = useCallback(() => {
        clearInactivityTimers();

        if (!currentUser) return;

        // Save current activity timestamp
        saveLastActivity();

        // Set logout timer
        inactivityTimerRef.current = setTimeout(() => {
            handleAutoLogout();
        }, INACTIVITY_TIMEOUT);
    }, [currentUser, clearInactivityTimers, handleAutoLogout, saveLastActivity]);

    // Handle user activity
    const handleUserActivity = useCallback(() => {
        if (currentUser) {
            // Save activity timestamp for cross-session tracking
            saveLastActivity();
            resetInactivityTimer();
        }
    }, [currentUser, resetInactivityTimer, saveLastActivity]);

    // Set up activity listeners
    useEffect(() => {
        if (!currentUser) {
            clearInactivityTimers();
            return;
        }

        // Reset timer on initial login
        resetInactivityTimer();

        // Activity events to track
        const events = [
            'mousedown',
            'mousemove',
            'keypress',
            'scroll',
            'touchstart',
            'click'
        ];

        // Add event listeners
        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, true);
        });

        // Cleanup
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity, true);
            });
            clearInactivityTimers();
        };
    }, [currentUser, handleUserActivity, resetInactivityTimer, clearInactivityTimers]);

    const value = {
        currentUser,
        loading,
        ready,
        signup,
        login,
        logout,
        refreshUser,
        resetInactivityTimer, // Expose for manual reset if needed
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
